import { EntityKindHandler, type EntityMenuContext, type AddPickerEntry, type ToolbarAction, type ToolbarActionContext, type EntityInteractionContext, type PointerDownGuardResult } from '../EntityKindHandler';
import type { CanvasEntity, ConnectorPosition } from '../../entityTypes';
import { ALL_CONNECTOR_SIDES, getConnectorPointForRect, getImageData } from '../../entityTypes';
import { CanvasImage } from './ImageNode';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Image, ToggleLeft, ToggleRight, Layers, Trash2 } from 'lucide-react';
import { buildLayerSubmenuItems } from '../../hooks/useLayerSubmenu';
import { buildGroupSubmenu, buildMultiDeleteItem, buildBorderColorSubmenu } from '../../lib/contextMenuHelpers';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { BORDER_COLOR_DEFAULTS, BORDER_WIDTH_OPTIONS } from '../../lib/edgeStyleConstants';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const OPACITY_OPTIONS = [
  { id: '1', label: '100%' },
  { id: '0.8', label: '80%' },
  { id: '0.6', label: '60%' },
  { id: '0.4', label: '40%' },
  { id: '0.2', label: '20%' },
];

const BORDER_RADIUS_OPTIONS = [
  { id: '0', label: t('canvasEntity.brNone') },
  { id: '4', label: t('canvasEntity.brSmall') },
  { id: '8', label: t('canvasEntity.brMedium') },
  { id: '16', label: t('canvasEntity.brLarge') },
];

export class ImageHandler extends EntityKindHandler {
  readonly kind = 'image';
  readonly Component = CanvasImage;
  readonly dataAttribute = 'data-canvas-image';
  canConnect = true;
  canResize = true;

  getAddPickerEntry(): AddPickerEntry {
    return { label: t('canvasEntity.image'), icon: Image, iconColor: '#0ea5e9', priority: 45, mode: 'instant' };
  }

  getConnectorPositions(entity: CanvasEntity): ConnectorPosition[] {
    if (!entity.connectable) return [];
    return ALL_CONNECTOR_SIDES.map(side => ({
      side,
      ...getConnectorPointForRect(entity.x, entity.y, entity.width, entity.height, side),
    }));
  }

  getToolbarActions(ctx: ToolbarActionContext): ToolbarAction[] {
    const { store, entity } = ctx;
    const d = getImageData(entity);
    return [
      {
        name: 'opacity',
        label: t('canvasEntity.opacity'),
        type: 'select',
        options: OPACITY_OPTIONS,
        getValue: () => String(d.opacity),
        apply: (v) => store.send({ type: 'image.updateData', entityId: entity.id, dataChanges: { opacity: Number(v) } }),
      },
      {
        name: 'borderRadius',
        label: t('canvasEntity.cornerRadius'),
        type: 'select',
        options: BORDER_RADIUS_OPTIONS,
        getValue: () => String(d.borderRadius),
        apply: (v) => store.send({ type: 'image.updateData', entityId: entity.id, dataChanges: { borderRadius: Number(v) } }),
      },
      {
        name: 'borderColor',
        label: t('canvasEntity.borderColor'),
        type: 'color',
        options: BORDER_COLOR_DEFAULTS.filter(c => c.color).map(c => ({ id: c.id, label: c.label, color: c.color })),
        getValue: () => d.borderColor ?? '',
        apply: (v) => {
          const color = v as string;
          if (!color) {
            store.send({ type: 'image.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'image.updateData', entityId: entity.id, dataChanges: { borderColor: color } });
          }
        },
      },
      {
        name: 'borderWidth',
        label: t('canvasEntity.borderWidth'),
        type: 'select',
        options: BORDER_WIDTH_OPTIONS,
        getValue: () => d.borderColor ? String(d.borderWidth ?? 1) : 'none',
        apply: (v) => {
          if (v === 'none') {
            store.send({ type: 'image.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'image.updateData', entityId: entity.id, dataChanges: { borderWidth: Number(v) } });
          }
        },
      },
      {
        name: 'connectable',
        label: 'Connectors',
        type: 'toggle',
        getValue: () => entity.connectable,
        apply: (v) => store.send({ type: 'entity.setConnectable', entityId: entity.id, connectable: !!v }),
      },
    ];
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity, layers } = ctx;
    const entityId = entity.id;
    const d = getImageData(entity);
    const items: ContextMenuItem[] = [];

    // --- Opazitaet submenu ---
    items.push({
      id: 'image-opacity-submenu',
      label: t('canvasEntity.opacity'),
      children: OPACITY_OPTIONS.map(opt => ({
        id: `op-${opt.id}`,
        label: `${d.opacity === Number(opt.id) ? '\u25CF ' : ''}${opt.label}`,
        onClick: () => store.send({ type: 'image.updateData', entityId, dataChanges: { opacity: Number(opt.id) } }),
      })),
    });

    // --- Eckenradius submenu ---
    items.push({
      id: 'image-borderradius-submenu',
      label: t('canvasEntity.cornerRadius'),
      children: BORDER_RADIUS_OPTIONS.map(opt => ({
        id: `br-${opt.id}`,
        label: `${d.borderRadius === Number(opt.id) ? '\u25CF ' : ''}${opt.label}`,
        onClick: () => store.send({ type: 'image.updateData', entityId, dataChanges: { borderRadius: Number(opt.id) } }),
      })),
    });

    // --- Border color submenu ---
    items.push(buildBorderColorSubmenu({
      id: 'border-color-submenu',
      label: t('canvasEntity.borderColor'),
      currentColor: d.borderColor,
      onSelectColor: (color) => store.send({ type: 'image.updateData', entityId, dataChanges: { borderColor: color } }),
      onRemoveBorder: () => store.send({ type: 'image.updateData', entityId, dataChanges: { borderColor: undefined, borderWidth: undefined } }),
    }));

    // --- Toggle connectors ---
    items.push({
      id: 'toggle-connectors',
      label: entity.connectable ? t('canvasEntity.connectorsOff') : t('canvasEntity.connectorsOn'),
      icon: entity.connectable ? ToggleRight : ToggleLeft,
      separator: true,
      onClick: () => store.send({ type: 'entity.setConnectable', entityId, connectable: !entity.connectable }),
    });

    // --- Gruppe submenu ---
    items.push({ ...buildGroupSubmenu(ctx), separator: true });

    // --- Layer submenu ---
    items.push({
      id: 'layer-submenu',
      label: 'Layer',
      icon: Layers,
      iconColor: '#4f46e5',
      children: buildLayerSubmenuItems(layers, entity.layerId, (layerId) => {
        store.send({ type: 'entity.setLayer', entityId, layerId });
      }),
    });

    // --- Remove ---
    items.push({
      id: 'remove',
      label: t('canvasEntity.remove'),
      icon: Trash2,
      iconColor: '#dc2626',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    });

    // --- Multi-delete ---
    const multiDelete = buildMultiDeleteItem(ctx, t('canvasEntity.elements'));
    if (multiDelete) {
      items.push(multiDelete);
    }

    return items;
  }

  onPointerDownGuard(_ctx: EntityInteractionContext, e: React.PointerEvent): PointerDownGuardResult | null {
    if ((e.target as HTMLElement).closest('[data-resize-handle]')) return { handled: true };
    return null;
  }

}
